import { sanitizeAnswerHtml } from './sanitize'
import { isBasemapLayer } from '../layer-access'

// What the app tells Artemis besides the question (WEB-7098): the conversation so far and
// what the map shows. Both are only sent in agent mode.

// The server keeps the same number of messages (ConversationHistory.MaxMessages).
export const MAX_HISTORY_MESSAGES = 20

const valueOf = v => (typeof v === 'function' ? v() : v)
const round = n => Math.round(n * 1e5) / 1e5

// { MapCenter: [lng, lat], MapZoom, MapBounds: [west, south, east, north], Bearing, OverlayLayerIds,
// BasemapLayerId }, the Artemis guide's Context shape, from a Google map or a MapLibre one. Null when
// the map can't say. overlayLayerIds: the data layers drawn now (see visibleOverlayLayerIds).
// basemapLayerId: the active basemap's layer id (81 SAT, 891 HYB, the user's selectedTopo for TOPO,
// 18 by default); the server turns it into a render key, so the client doesn't map it.
// A map's starting view (WEB-7172): where it was made, before the user or the app moved it. Until the map
// leaves it, its view says nothing about where the user is looking, so the question leaves the view out
// and Artemis falls back to the selection, the home state or asking. Without this, the full view's
// scratchpad map sent its WY/MT/ID start (-110, 44, zoom 5) with a third of web questions.
const START_VIEW = '__artemisStartView'
export const markStartView = (map, view) => { if (map && view) map[START_VIEW] = view }

// True while the map is still at the view markStartView recorded.
export const atStartView = map => {
  const start = map && map[START_VIEW]
  if (!start) return false
  try {
    const c = map.getCenter()
    const lat = valueOf(c.lat), lng = valueOf(c.lng), zoom = map.getZoom ? map.getZoom() : null
    return Math.abs(lat - start.lat) < 1e-6 && Math.abs(lng - start.lng) < 1e-6 && zoom === start.zoom
  } catch (e) {
    return false
  }
}

// The map's view and layers for a question; null when there's nothing to send. A map still at its starting
// view (atStartView) sends only its layers.
export const mapContext = (map, { overlayLayerIds, basemapLayerId } = {}) => {
  if (atStartView(map)) {
    const layers = {}
    if (overlayLayerIds && overlayLayerIds.length) layers.OverlayLayerIds = overlayLayerIds.slice(0, MAX_OVERLAY_LAYER_IDS)
    if (Number.isInteger(basemapLayerId)) layers.BasemapLayerId = basemapLayerId
    return Object.keys(layers).length ? layers : null
  }
  try {
    const center = map && map.getCenter && map.getCenter()
    if (!center) return null
    const lat = valueOf(center.lat), lng = valueOf(center.lng)
    if (!isFinite(lat) || !isFinite(lng)) return null

    const context = { MapCenter: [round(lng), round(lat)] }
    const zoom = map.getZoom && map.getZoom()
    if (isFinite(zoom)) context.MapZoom = Math.round(zoom * 100) / 100

    const bounds = map.getBounds && map.getBounds()
    let box = null
    if (bounds && bounds.getNorthEast) {
      const ne = bounds.getNorthEast(), sw = bounds.getSouthWest()
      box = [sw.lng(), sw.lat(), ne.lng(), ne.lat()].map(round)
    } else if (bounds && bounds.getWest) {
      box = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].map(round)
    }
    // A hidden map (the main map under the ARTEMIS tab) has no size, so its bounds are a point: leave them out.
    if (box && box[0] !== box[2] && box[1] !== box[3]) context.MapBounds = box

    const bearing = map.getHeading ? map.getHeading() : map.getBearing ? map.getBearing() : undefined
    if (isFinite(bearing)) context.Bearing = Math.round(bearing * 10) / 10

    if (overlayLayerIds && overlayLayerIds.length) context.OverlayLayerIds = overlayLayerIds.slice(0, MAX_OVERLAY_LAYER_IDS)
    if (Number.isInteger(basemapLayerId)) context.BasemapLayerId = basemapLayerId
    return context
  } catch (e) {
    return null
  }
}

// The server reads at most this many (QueryController.MaxOverlayLayerIds).
export const MAX_OVERLAY_LAYER_IDS = 50

// The basemap the map is drawing, as a layer id, or null.
export const activeBasemapLayerId = (activeLayerIds = [], layersDictionary = {}) => {
  const id = activeLayerIds.find(layerId => layersDictionary[layerId] && isBasemapLayer(layersDictionary[layerId]))
  return id === undefined ? null : id
}

// The active data layers the map is drawing: none while the eye toggle hides them. Basemaps
// are left out; they go by their own key.
export const visibleOverlayLayerIds = (activeLayerIds = [], layersDictionary = {}, layersVisible = true) =>
  layersVisible
    ? activeLayerIds.filter(id => layersDictionary[id] && !isBasemapLayer(layersDictionary[id]))
    : []

// An answer's HTML as plain text, keeping paragraphs and list items apart.
export const answerToText = html => {
  const doc = new window.DOMParser().parseFromString(sanitizeAnswerHtml(html), 'text/html')
  doc.querySelectorAll('li').forEach(li => li.prepend('- '))
  doc.querySelectorAll('br').forEach(br => br.replaceWith('\n'))
  doc.querySelectorAll('p, li').forEach(el => el.append('\n\n'))
  return doc.body.textContent.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
}

// The chat so far as [{ role, content }], oldest first. Only replies with text count; a
// question whose reply has none is left out too, so the roles alternate.
export const chatHistory = messages => {
  const history = []
  messages.forEach(message => {
    if (message.status === 'streaming') return
    const answer = message.answer ? answerToText(message.answer) : ''
    const question = (message.question || '').trim()
    if (!answer || !question) return
    history.push({ role: 'user', content: question }, { role: 'assistant', content: answer })
  })
  return history.slice(-MAX_HISTORY_MESSAGES)
}

// The first [lng, lat] in GeoJSON coordinates, however deeply nested.
const firstCoordinate = coords => {
  let c = coords
  while (Array.isArray(c) && Array.isArray(c[0])) c = c[0]
  return Array.isArray(c) && c.length >= 2 && isFinite(c[0]) && isFinite(c[1]) ? [round(c[0]), round(c[1])] : null
}

const shapePoint = shape => {
  try {
    const geometry = JSON.parse(shape.geoJson).features[0].geometry
    return firstCoordinate(geometry.coordinates)
  } catch (e) {
    return null
  }
}

// The object the user has open on the map (selectedObjId), as Context.Selection (API-3642), with its
// name for the composer chip: { selection: { Kind, Id, Point }, name }. Null when it isn't one of
// theirs. The map only sets an id, so the kind comes from which list holds it.
export const selectionFor = (id, { markers = [], lines = [], polygons = [], circles = [] } = {}) => {
  if (id == null) return null
  const marker = (markers || []).find(m => m.id === id)
  if (marker) {
    const point = isFinite(marker.longitude) && isFinite(marker.latitude) ? [round(marker.longitude), round(marker.latitude)] : null
    return { selection: { Kind: 'marker', Id: id, ...(point ? { Point: point } : {}) }, name: marker.name || 'Waypoint' }
  }
  const shape = [...(lines || []), ...(polygons || []), ...(circles || [])].find(s => s.id === id)
  if (shape) {
    const point = shapePoint(shape)
    return { selection: { Kind: 'shape', Id: id, ...(point ? { Point: point } : {}) }, name: shape.name || 'Shape' }
  }
  return null
}

// The user's home state as two letters (Context.HomeState), from their profile's stateId, or null.
// Artemis treats them as a resident there and a nonresident elsewhere, e.g. for draw odds.
export const homeStateCode = (user, states = []) => {
  const id = user && user.stateId
  if (id == null || !Array.isArray(states)) return null
  const state = states.find(s => s && s.id === id)
  const code = state && typeof state.abbreviation === 'string' ? state.abbreviation.trim().toUpperCase() : ''
  return /^[A-Z]{2}$/.test(code) ? code : null
}
